/**
 * The wire side of the exam-wrapper sets (pedagogy spec §7.3.5, §7.3.6): the two question fetches,
 * the exam history the timed pair's pace falls back on, and `POST /api/practice/plan`. Plain
 * functions for the starter (it runs once, from an effect), a mutation for the page.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@client/api/client";
import { practiceKeys } from "../api";
import type { PracticeQuestionsResponse } from "../types";
import type { PaceEntry } from "./timedPair";

/** The query string of the "Vilken princip?" fetch (`planOnly` is mixed mode only). */
export function planOnlyQuery(chapters: readonly number[], limit: number): string {
  const search = new URLSearchParams({ mode: "mixed", planOnly: "true" });
  if (chapters.length > 0) search.set("chapters", chapters.join(","));
  search.set("limit", String(limit));
  return search.toString();
}

/** The timed pair draws from ten open problems in mixed mode and keeps the two worth most. */
export const TIMED_CANDIDATES = 10;

export function timedPairQuery(chapters: readonly number[]): string {
  const search = new URLSearchParams({ mode: "mixed", types: "open" });
  if (chapters.length > 0) search.set("chapters", chapters.join(","));
  search.set("limit", String(TIMED_CANDIDATES));
  return search.toString();
}

export function fetchQuestions(query: string) {
  return api.get<PracticeQuestionsResponse>(`/api/practice/questions?${query}`);
}

/** The student's exam sittings, newest first; read only when a timed-pair link carries no pace. */
export async function fetchExamPace(): Promise<PaceEntry[]> {
  const res = await api.get<{ history: PaceEntry[] }>("/api/exam/history?limit=20");
  return res.history;
}

/** `POST /api/practice/plan` body (spec §7.3.5). */
export interface PostPlanBody {
  questionId: string;
  topicId: string;
  origin?: { kind: "exam-wrapper"; set: "which-principle"; examSessionId?: string };
}

/** What the pick came to; `outcome`, not the gated `correct`. */
export interface PostPlanResponse {
  attemptId: string;
  outcome: "right" | "wrong";
  targetTopicId: string;
}

/**
 * One pick, posted at once. It lands a row, so the practice caches ("Senaste övningar" and the
 * rest) are marked stale like after an answer.
 */
export function usePostPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PostPlanBody) => api.post<PostPlanResponse>("/api/practice/plan", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: practiceKeys.all }),
  });
}
